import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import OneDriveIcon from 'shared/assets/images/onedrive.svg';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { registerDriveFolder } from 'store/organisation/actions';
import { OneDriveConsts } from './one-drive-const';
import IconButton from 'components/Custom/IconButton';

interface OneDrivePickerProps {
  connectionId: string;
  folderName: string | null;
  drivePath: string;
  syncExistingFiles: boolean;
  /** Render a custom trigger instead of the default icon button. Receives the picker's onClick handler. */
  renderTrigger?: (args: { onClick: () => void }) => React.ReactNode;
}

const OneDrivePicker: React.FC<OneDrivePickerProps> = ({
  connectionId,
  folderName,
  drivePath,
  syncExistingFiles,
  renderTrigger,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const winName = `oneDrivePicker_${connectionId}`;
  const syncRef = useRef(syncExistingFiles);

  const popupRef = useRef<Window | null>(null);
  const portRef = useRef<MessagePort | null>(null);

  useEffect(() => {
    syncRef.current = syncExistingFiles;
  }, [syncExistingFiles]);

  const openPicker = (tokenToUse: string, site: string, _email: string) => {
    const winName = `oneDrivePicker_${connectionId}`;

    if (!popupRef.current || popupRef.current.closed) {
      popupRef.current = window.open('', winName, 'width=800,height=700');
    }

    const pickerOptions = {
      sdk: '8.0',
      action: 'pick',
      entry: { oneDrive: { files: { select: { type: 'folder' } } } },
      typesAndSources: { mode: 'folders', pivots: { oneDrive: true } },
      messaging: { origin: window.location.origin, channelId: '27' },
    };

    const queryString = new URLSearchParams({
      filePicker: JSON.stringify(pickerOptions),
    }).toString();

    const isPersonal = site === OneDriveConsts.PERSONAL_DOMAIN;
    const path = isPersonal ? '/picker' : '/_layouts/15/FilePicker.aspx';
    const url = `${site}${path}?${queryString}`;

    const form = document.createElement('form');
    form.style.display = 'none';
    form.setAttribute('action', url);
    form.setAttribute('method', 'POST');
    form.setAttribute('target', winName);

    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', 'access_token');
    input.setAttribute('value', tokenToUse);

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  useEffect(() => {
    const handleGlobalMessages = (event: MessageEvent) => {
      if (event.data?.type === 'MS_AUTH_SUCCESS') {
        const receivedToken = event.data.accessToken;
        const site = event.data.site;
        const email = event.data.userEmail;
        openPicker(receivedToken, site, email);
      }

      if (
        event.data.type === 'MS_AUTH_ERROR' ||
        event.data.type === 'MS_AUTH_ERROR_ACCESS_DENIED'
      ) {
        const errorMessage =
          event.data.type === 'MS_AUTH_ERROR_ACCESS_DENIED'
            ? t('driveScanner.oneDriveAuthAccessDenied')
            : t('driveScanner.oneDriveAuthError');
        const message: MessageData = {
          title: t('common.error'),
          description: errorMessage,
          type: MessageStates.ERROR,
          duration: 10,
        };
        dispatch(turnMessageOn(message));
      }

      if (event.data?.type === 'initialize' && event.data?.channelId === '27') {
        const port = event.ports[0];
        portRef.current = port;
        port.addEventListener('message', (ev) => {
          if (ev.data.type === 'command') {
            if (ev.data.data.command === 'pick') {
              dispatch(
                registerDriveFolder(
                  ev.data.data.items[0].id,
                  connectionId,
                  ev.data.data.items[0].parentReference.driveId,
                  'one',
                  syncRef.current
                )
              );
              popupRef.current?.close();
            } else if (ev.data.data.command === 'close') {
              popupRef.current?.close();
            }
          }
        });

        port.start();
        port.postMessage({ type: 'activate' });
      }
    };

    window.addEventListener('message', handleGlobalMessages);
    return () => window.removeEventListener('message', handleGlobalMessages);
  }, []);

  const startFlow = () => {
    const state = {
      host: OneDriveConsts.HOST_DOMAIN,
      connectionId: connectionId,
    };

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${
      OneDriveConsts.CLIENT_ID
    }&response_type=code&redirect_uri=${
      OneDriveConsts.REDIRECT_URI
    }&response_mode=query&scope=${OneDriveConsts.SCOPE}&state=${btoa(
      JSON.stringify(state)
    )}&prompt=select_account`;

    const win = window.open(authUrl, winName, 'width=600,height=600');
    popupRef.current = win;
  };

  if (renderTrigger) {
    return <>{renderTrigger({ onClick: startFlow })}</>;
  }

  const label = t('organisation.receipts-connect-folder-onedrive');
  return (
    <IconButton
      onClick={startFlow}
      icon={OneDriveIcon}
      alt={label}
      tooltip={label}
      ariaLabel={label}
    />
  );
};

export default OneDrivePicker;
